import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Adds the `.is-visible` class to elements with the `.reveal` class
 * once they enter the viewport. Re-runs on route changes so newly
 * mounted page sections are observed.
 *
 * IMPORTANT: do not apply `.reveal` to conditionally-rendered nodes
 * (the observer only runs on mount / route change).
 */
export function useReveal() {
    const location = useLocation();
    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") return undefined;
        document.documentElement.classList.add("js-reveal-ready");
        const els = document.querySelectorAll(".reveal");
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: "0px 0px -5% 0px" },
        );
        els.forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                el.classList.add("is-visible");
            } else {
                io.observe(el);
            }
        });
        return () => {
            io.disconnect();
            document.documentElement.classList.remove("js-reveal-ready");
        };
        // Re-runs only on route changes. All other identifiers used inside
        // the effect (io, els, el) are created locally; IntersectionObserver
        // and window are globals — none belong in the deps array.
    }, [location.pathname]);
}
